// Local dashboard aggregation. Phase 35g.
//
// Mirrors the server's server/dashboard.js aggregations but runs in
// the browser over the IndexedDB transactions table. Eliminates the
// 10-15 server round-trips per dashboard render — now it's one
// IndexedDB read + in-JS aggregation.
//
// Range modes: 'day' (single YYYY-MM-DD), 'month' (YYYY-MM), 'year' (YYYY).
// Optional categoryId filter scopes all aggregates to that one category.

import { data } from './dataService.js';

// Date helpers — duplicated from dashboard.js to keep this module
// self-contained (no server-side code in the browser).
export function ymKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}
export function ymdKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function shiftYm(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  return ymKey(new Date(y, m - 1 + delta, 1));
}
function shiftYmd(ymd, delta) {
  const [y, m, d] = ymd.split('-').map(Number);
  return ymdKey(new Date(y, m - 1, d + delta));
}
function deltaPct(current, prior) {
  if (prior === 0) return current === 0 ? 0 : (current > 0 ? 1 : -1);
  return (current - prior) / prior;
}

// Filter a transaction list to the active period + optional category.
// Phase 70: range === '5y' filters across the rolling 5-year window
//   [endYear-4 .. endYear], where endYear = Number(ym).
function filterTxs(txs, ym, range, categoryId) {
  return txs.filter(t => {
    if (t.deleted) return false;
    if (categoryId && t.category_id !== categoryId) return false;
    if (range === 'day')  return t.occurred_on === ym;
    if (range === 'month') return t.occurred_on && t.occurred_on.startsWith(ym);
    if (range === 'year') {
      const [y] = ym.split('-');
      return t.occurred_on && t.occurred_on.startsWith(y + '-');
    }
    if (range === '5y') {
      const endY = Number(ym);
      const startY = endY - 4;
      const y = t.occurred_on ? Number(t.occurred_on.slice(0, 4)) : NaN;
      return Number.isFinite(y) && y >= startY && y <= endY;
    }
    return true;
  });
}

function splitSum(txs) {
  let income = 0, expense = 0;
  for (const t of txs) {
    if (t.type === 'income')  income  += Number(t.amount || 0);
    if (t.type === 'expense') expense += Number(t.amount || 0);
  }
  return { income, expense };
}

// ─── KPI ────────────────────────────────────────────────────────────────

function computeKpi(txs, ym, range, categoryId) {
  const cur = splitSum(filterTxs(txs, ym, range, categoryId));
  let prior, priorYmd;
  if (range === 'day') {
    priorYmd = shiftYmd(ym, -1);
    prior = splitSum(filterTxs(txs, priorYmd, 'day', categoryId));
    const priorSavingsRate = prior.income > 0
      ? (prior.income - prior.expense) / prior.income
      : null;
    return {
      income_month: cur.income,
      expense_month: cur.expense,
      net_month: cur.income - cur.expense,
      savings_rate: cur.income > 0 ? (cur.income - cur.expense) / cur.income : 0,
      vs_last_month: {
        income: deltaPct(cur.income, prior.income),
        expense: deltaPct(cur.expense, prior.expense),
        net: deltaPct(cur.income - cur.expense, prior.income - prior.expense),
        savings_rate: priorSavingsRate,
      },
      period_label: '今日',
    };
  }
  if (range === 'year') {
    // Phase 77f: compare against the previous calendar year (offset -1).
    // Same logic as month mode but bucketed by year instead of month.
    const endY = Number(ym);
    const priorY = String(endY - 1);
    prior = splitSum(filterTxs(txs, priorY, 'year', categoryId));
    const priorSavingsRate = prior.income > 0
      ? (prior.income - prior.expense) / prior.income
      : null;
    return {
      income_month: cur.income,
      expense_month: cur.expense,
      net_month: cur.income - cur.expense,
      savings_rate: cur.income > 0 ? (cur.income - cur.expense) / cur.income : 0,
      vs_last_month: {
        income: deltaPct(cur.income, prior.income),
        expense: deltaPct(cur.expense, prior.expense),
        net: deltaPct(cur.income - cur.expense, prior.income - prior.expense),
        savings_rate: priorSavingsRate,
      },
      period_label: '全年',
    };
  }
  if (range === '5y') {
    // Phase 70: rolling 5-year window. Prior = the 5 years BEFORE
    // the current window (offset by -5). No savings vs_last_month for
    // 5y (compare is less meaningful at this scale — users want the
    // total, not delta).
    const endY = Number(ym);
    const priorEndY = endY - 5;
    prior = splitSum(filterTxs(txs, String(priorEndY), '5y', categoryId));
    const priorSavingsRate = prior.income > 0
      ? (prior.income - prior.expense) / prior.income
      : null;
    return {
      income_month: cur.income,
      expense_month: cur.expense,
      net_month: cur.income - cur.expense,
      savings_rate: cur.income > 0 ? (cur.income - cur.expense) / cur.income : 0,
      vs_last_month: {
        income: deltaPct(cur.income, prior.income),
        expense: deltaPct(cur.expense, prior.expense),
        net: deltaPct(cur.income - cur.expense, prior.income - prior.expense),
        savings_rate: priorSavingsRate,
      },
      period_label: '近 5 年',
    };
  }
  // month
  const priorYm = shiftYm(ym, -1);
  prior = splitSum(filterTxs(txs, priorYm, 'month', categoryId));
  const priorSavingsRate = prior.income > 0
    ? (prior.income - prior.expense) / prior.income
    : null;
  return {
    income_month: cur.income,
    expense_month: cur.expense,
    net_month: cur.income - cur.expense,
    savings_rate: cur.income > 0 ? (cur.income - cur.expense) / cur.income : 0,
    vs_last_month: {
      income: deltaPct(cur.income, prior.income),
      expense: deltaPct(cur.expense, prior.expense),
      net: deltaPct(cur.income - cur.expense, prior.income - prior.expense),
      savings_rate: priorSavingsRate,
    },
    period_label: '本月',
  };
}

// ─── Monthly Trend ──────────────────────────────────────────────────────

// Phase 71: optional `priorOffsetYears` — when > 0, also compute the same
// bucket span offset backwards by N years. Used by year mode (-1) and 5y
// mode (-5) to overlay last-year / last-5y data on the trend chart.
// Returns `{ current: [...], prior: [...] | null }`. Each row in `prior`
// has the same ym as the current bucket but the income/expense/net come
// from N years earlier.
// Phase 72: when `mode === 'overlay'`, return a different shape suited
// for the "By Year" view (12-month X axis, one line per year):
//   `{ overlay: { years: [2022..2026], rows: [{ month: 1, y2022: ..., y2023: ..., ... }, ...] } | null }`
function computeMonthlyTrend(txs, ym, months = 6, categoryId, priorOffsetYears = 0, mode = 'timeline') {
  const buckets = [];
  for (let i = months - 1; i >= 0; i--) buckets.push(shiftYm(ym, -i));
  const sums = Object.fromEntries(buckets.map(b => [b, { income: 0, expense: 0 }]));
  const priorBuckets = priorOffsetYears > 0
    ? buckets.map(b => shiftYm(b, -12 * priorOffsetYears))
    : null;
  const priorSums = priorBuckets
    ? Object.fromEntries(priorBuckets.map(b => [b, { income: 0, expense: 0 }]))
    : null;
  for (const t of txs) {
    if (t.deleted) continue;
    if (categoryId && t.category_id !== categoryId) continue;
    if (!t.occurred_on) continue;
    const ym7 = t.occurred_on.slice(0, 7);
    if (sums[ym7]) {
      if (t.type === 'income') sums[ym7].income  += Number(t.amount || 0);
      if (t.type === 'expense') sums[ym7].expense += Number(t.amount || 0);
    }
    if (priorSums && priorSums[ym7]) {
      if (t.type === 'income') priorSums[ym7].income  += Number(t.amount || 0);
      if (t.type === 'expense') priorSums[ym7].expense += Number(t.amount || 0);
    }
  }
  const current = buckets.map(b => ({
    ym: b,
    income: sums[b].income,
    expense: sums[b].expense,
    net: sums[b].income - sums[b].expense,
  }));
  let prior = null;
  if (priorBuckets) {
    prior = buckets.map((b, i) => {
      const p = priorBuckets[i];
      return {
        ym: b,
        prior_ym: p,
        prior_income: priorSums[p].income,
        prior_expense: priorSums[p].expense,
        prior_net: priorSums[p].income - priorSums[p].expense,
      };
    });
  }
  // Phase 72: overlay mode — bucket each year's transactions by month (1..12),
  // producing one value per (month, year). Used by year + 5y chart views.
  let overlay = null;
  if (mode === 'overlay' && months > 0) {
    // The end year is determined by the last bucket (ym of the rightmost point).
    const endY = Number(buckets[buckets.length - 1].slice(0, 4));
    // 5y mode → 5 distinct years (endY-4..endY). Year mode → 2 years
    // (endY-1 + endY, same idea as priorOffsetYears=1 but both rendered
    // as solid lines so user can compare this year vs last year head-to-head).
    const span = priorOffsetYears > 0 ? priorOffsetYears + 1 : 1;
    const startY = endY - (span - 1);
    const years = [];
    for (let y = startY; y <= endY; y++) years.push(y);
    // For each (month, year) cell, sum income + expense from txs.
    // Initialize 12×N grid.
    const grid = {}; // key: `${year}-${m}` → { income, expense }
    for (const y of years) for (let m = 1; m <= 12; m++) grid[`${y}-${m}`] = { income: 0, expense: 0 };
    for (const t of txs) {
      if (t.deleted) continue;
      if (categoryId && t.category_id !== categoryId) continue;
      if (!t.occurred_on) continue;
      const y = Number(t.occurred_on.slice(0, 4));
      const m = Number(t.occurred_on.slice(5, 7));
      const key = `${y}-${m}`;
      if (grid[key]) {
        if (t.type === 'income')  grid[key].income  += Number(t.amount || 0);
        if (t.type === 'expense') grid[key].expense += Number(t.amount || 0);
      }
    }
    // Build rows: one per month (1..12). Each row has income_y / expense_y
    // for every year in `years`. Row key for X axis = month number.
    const rows = [];
    for (let m = 1; m <= 12; m++) {
      const row = { month: m };
      for (const y of years) {
        row[`income_${y}`]  = grid[`${y}-${m}`].income;
        row[`expense_${y}`] = grid[`${y}-${m}`].expense;
        row[`net_${y}`]     = grid[`${y}-${m}`].income - grid[`${y}-${m}`].expense;
      }
      rows.push(row);
    }
    overlay = { years, rows };
  }
  return { current, prior, overlay };
}

// ─── Category Breakdown ─────────────────────────────────────────────────

// Phase 77d: Daily trend for the Day range filter.
// Buckets transactions by DATE (YYYY-MM-DD) for the past N days
// anchored on the day in `anchorYmd` (also YYYY-MM-DD). Returns the
// same shape as computeMonthlyTrend's `current` array, but with the
// `ym` field set to the full YYYY-MM-DD string — so TrendChart's
// monthLabel(ym) returns "M/D" instead of a month name. No prior
// (YoY would need a year of data and would be cluttered for 7 days).
function computeDailyTrend(txs, anchorYmd, days = 7, categoryId) {
  const buckets = [];
  const anchor = new Date(`${anchorYmd}T00:00:00`);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() - i);
    buckets.push(ymdKey(d));
  }
  const sums = Object.fromEntries(buckets.map(b => [b, { income: 0, expense: 0 }]));
  for (const t of txs) {
    if (t.deleted) continue;
    if (categoryId && t.category_id !== categoryId) continue;
    if (!t.occurred_on) continue;
    if (sums[t.occurred_on]) {
      if (t.type === 'income')  sums[t.occurred_on].income  += Number(t.amount || 0);
      if (t.type === 'expense') sums[t.occurred_on].expense += Number(t.amount || 0);
    }
  }
  const current = buckets.map(b => ({
    ym: b,
    income: sums[b].income,
    expense: sums[b].expense,
    net: sums[b].income - sums[b].expense,
  }));
  return { current, prior: null, overlay: null };
}

function computeCategoryBreakdown(txs, categories, ym, type, categoryId) {
  // Single category filter: return just that one with pct:1.
  if (categoryId) {
    const filtered = filterTxs(txs, ym, 'month', categoryId).filter(t => t.type === type);
    const total = filtered.reduce((s, t) => s + Number(t.amount || 0), 0);
    if (total === 0) return [];
    const cat = categories.find(c => (c.local_uuid || c.id) === categoryId);
    if (!cat) return [];
    return [{
      category_id: cat.local_uuid || cat.id,
      name: cat.name,
      icon: cat.icon,
      color: cat.color,
      amount: total,
      pct: 1,
    }];
  }
  const filtered = filterTxs(txs, ym, 'month', null).filter(t => t.type === type);
  const byCat = new Map();
  for (const t of filtered) {
    const cid = t.category_id;
    if (!cid) continue;
    byCat.set(cid, (byCat.get(cid) || 0) + Number(t.amount || 0));
  }
  const total = Array.from(byCat.values()).reduce((s, v) => s + v, 0);
  if (total === 0) return [];
  const out = [];
  for (const cat of categories) {
    if (cat.type !== type) continue;
    const cid = cat.local_uuid || cat.id;
    const amount = byCat.get(cid) || 0;
    if (amount === 0) continue;
    out.push({
      category_id: cid,
      name: cat.name,
      icon: cat.icon,
      color: cat.color,
      amount,
      pct: amount / total,
    });
  }
  out.sort((a, b) => b.amount - a.amount);
  return out;
}

// ─── Budget Alerts ──────────────────────────────────────────────────────

// filterTxsByPeriod: reuses the same predicate as the KPI / category
// breakdown path so the Budget alerts panel matches the active period on
// Dashboard. Phase 49b: previously hard-coded to the *current* month
// (`ymNow`), which made historical/navigated periods show stale data.
function filterTxsByPeriod(txs, ym, range, categoryId) {
  return txs.filter(t => {
    if (t.deleted) return false;
    if (categoryId && t.category_id !== categoryId) return false;
    if (range === 'day')   return t.occurred_on === ym;
    if (range === 'month') return t.occurred_on && t.occurred_on.startsWith(ym);
    if (range === 'year')  {
      const [y] = ym.split('-');
      return t.occurred_on && t.occurred_on.startsWith(y + '-');
    }
    if (range === '5y') {
      // Phase 70: rolling 5-year window. Same predicate as filterTxs().
      const endY = Number(ym);
      const startY = endY - 4;
      const y = t.occurred_on ? Number(t.occurred_on.slice(0, 4)) : NaN;
      return Number.isFinite(y) && y >= startY && y <= endY;
    }
    return true;
  });
}

function computeBudgetAlerts(txs, categories, budgets, ym, range, categoryId) {
  // Use the active period (not the current month) so the bar pct reflects
  // what the user is currently viewing. Phase 49b: this used to ignore
  // ym/range and always sum the current month — historical navigation
  // showed mismatched data. Period shape rules:
  //   day   → 'YYYY-MM-DD'   → exact match
  //   month → 'YYYY-MM'       → startsWith
  //   year  → 'YYYY'          → startsWith('YYYY-')
  const limitMult = range === 'year' ? 12 : range === '5y' ? 60 : range === 'day' ? (1 / 30) : 1;
  const rows = [];
  for (const b of budgets) {
    if (b.deleted) continue;
    if (categoryId && b.category_id !== categoryId) continue;
    const cat = categories.find(c =>
      c.id === b.category_id || c.local_uuid === b.category_id || c.server_id === b.category_id
    );
    if (!cat) continue;
    // filterTxsByPeriod already filters by period + category + range;
    // we only need to filter by `type = expense` and the specific
    // category (since `categoryId` filter was for ALL categories, not
    // THIS category). Phase 49b: previously also did
    // `startsWith(ymNow)` which silently dropped non-current-month
    // data.
    const spent = filterTxsByPeriod(txs, ym, range, categoryId)
      .filter(t => t.category_id === (cat.local_uuid || cat.id)
                  && t.type === 'expense')
      .reduce((s, t) => s + Number(t.amount || 0), 0);
    const effectiveLimit = b.monthly_limit * limitMult;
    const pct = effectiveLimit > 0 ? spent / effectiveLimit : 0;
    let level = 'ok';
    if (pct >= 1) level = 'over';
    else if (pct >= 0.8) level = 'warn';
    rows.push({
      category_id: cat.local_uuid || cat.id,
      name: cat.name,
      icon: cat.icon,
      color: cat.color,
      limit: effectiveLimit,
      spent,
      pct,
      level,
    });
  }
  rows.sort((a, b) => b.pct - a.pct);
  return rows;
}

// ─── Top categories (last 30 days) ──────────────────────────────────────

function computeTopCategories30d(txs, categories) {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 30);
  const fromStr = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
  const filt = txs.filter(t => !t.deleted && t.type === 'expense' && t.occurred_on >= fromStr);
  const byCat = new Map();
  for (const t of filt) {
    if (!t.category_id) continue;
    byCat.set(t.category_id, (byCat.get(t.category_id) || 0) + Number(t.amount || 0));
  }
  const out = [];
  for (const cat of categories) {
    const amount = byCat.get(cat.local_uuid || cat.id) || 0;
    if (amount === 0) continue;
    out.push({ category_id: cat.local_uuid || cat.id, name: cat.name, icon: cat.icon, color: cat.color, amount });
  }
  out.sort((a, b) => b.amount - a.amount);
  return out.slice(0, 5);
}

// ─── Credit cards (Phase 35g: local enrichment) ────────────────────────
//
// Mirrors server/routes.js computeDueInfo() in pure JS so the Dashboard's
// reminder card can render days-until-due / grace / overdue / urgency
// without a server round-trip.
//
// Outstanding amount auto-calc (the SUM-since-last-statement logic on the
// server) needs a tx↔card join that's not modeled locally — we use the
// stored `outstanding_amount` as-is. Card expiry / statement_day /
// due_day / last_paid_at all come from IndexedDB.

function pad2(n) { return String(n).padStart(2, '0'); }
function toIso(y, m0, d) { return `${y}-${pad2(m0 + 1)}-${pad2(d)}`; }
function dateOf(y, m0, d) { return new Date(y, m0, d); }

function lastOccurrenceOnOrBefore(today, day) {
  const y = today.getFullYear();
  const m = today.getMonth();
  if (today.getDate() >= day) return dateOf(y, m, day);
  return dateOf(y, m - 1, day);
}

function dueDateForStatement(stmtDate, statement_day, due_day) {
  const y = stmtDate.getFullYear();
  const m = stmtDate.getMonth();
  if (statement_day <= due_day) return dateOf(y, m, due_day);
  return dateOf(y, m + 1, due_day);
}

export function computeDueInfo(card, today = new Date(), txs = []) {
  if (!card) return card;
  // Cards with no due_day configured don't participate in reminders.
  if (!card.due_day) return { ...card, days_until_due: null, next_due_iso: null, urgency: 'ok' };
  const msPerDay = 24 * 60 * 60 * 1000;
  const statement_day = card.statement_day || 1;
  const due_day = card.due_day;
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const cycleStart = lastOccurrenceOnOrBefore(todayStart, statement_day);
  let cycleEnd = dueDateForStatement(cycleStart, statement_day, due_day);

  let nextDue = cycleEnd;
  if (cycleEnd < todayStart) {
    const nextStmt = dateOf(cycleStart.getFullYear(), cycleStart.getMonth() + 1, statement_day);
    nextDue = dueDateForStatement(nextStmt, statement_day, due_day);
    cycleEnd = dueDateForStatement(cycleStart, statement_day, due_day); // keep current cycle window
  }
  const daysUntil = Math.round((nextDue - todayStart) / msPerDay);

  // Paid-in-cycle detection (mirrors server).
  let paid_in_cycle = false;
  if (card.last_paid_at) {
    const paidDate = new Date(card.last_paid_at.length === 10
      ? card.last_paid_at + 'T00:00:00'
      : card.last_paid_at);
    if (!Number.isNaN(paidDate.getTime())
        && paidDate >= cycleStart && paidDate <= todayStart) {
      paid_in_cycle = true;
    }
  }

  const cycle_start_iso = toIso(cycleStart.getFullYear(), cycleStart.getMonth(), cycleStart.getDate());
  const cycle_end_iso   = toIso(cycleEnd.getFullYear(), cycleEnd.getMonth(), cycleEnd.getDate());

  const sameMonthBilling = statement_day <= due_day;
  const inGap = sameMonthBilling && todayStart.getDate() < statement_day;
  const inGraceWindow = !inGap && todayStart >= cycleStart && todayStart <= cycleEnd;
  let grace_days_remaining = null;
  let is_overdue = false;
  if (inGraceWindow && !paid_in_cycle) {
    grace_days_remaining = Math.max(0, Math.round((cycleEnd - todayStart) / msPerDay));
  } else if (!inGap && todayStart > cycleEnd) {
    is_overdue = true;
  }
  if (paid_in_cycle) is_overdue = false;

  let urgency = 'ok';
  if (!paid_in_cycle) {
    if (daysUntil <= 3) urgency = 'critical';
    else if (daysUntil <= 7) urgency = 'warn';
  }

  // Auto-compute outstanding_amount as SUM of all expenses tagged to
  // this card that haven't been paid yet. Phase 49g: Phase 49f used
  // a cycle_start cutoff (matching the server's billing-cycle SQL),
  // but that excluded history transactions the user logged with
  // occurred_on BEFORE the current cycle's statement closing — those
  // are still real debt on the card and should count toward
  // "amount you need to pay back". The server uses cycle_start
  // because it's billing-cycle-aware; the dashboard widget's
  // "outstanding" means "total owed right now", which is the sum of
  // every charge since the card was last paid (or since tracking
  // started, whichever is later).
  //
  // Algorithm:
  //   1. Start with the user-set baseline (card.outstanding_amount
  //      they entered in Settings → Credit Cards to record pre-app
  //      debt). This is the "carry-over" amount.
  //   2. Add SUM of all expense txs tagged to this card via EITHER
  //      credit_card_local_uuid (Phase 49e canonical local ref) OR
  //      credit_card_id (server FK).
  //   3. If the card has been paid (last_paid_at), drop any charges
  //      with occurred_on < last_paid_at — those were settled by
  //      the payment and shouldn't double-count.
  //
  // Dedupe via Set keyed by tx-local-uuid so a tx carrying both
  // identifiers doesn't double-count.
  // outstanding_amount = user-set baseline (carry-over debt) +
  // SUM of unpaid charges since last payment (or all charges if
  // never paid). This handles three cases:
  //   1. Pre-tracking debt: user enters $5000 in Settings to
  //      represent unpaid balance before using the app.
  //   2. Current cycle charges: tx with occurred_on >= cycle start.
  //   3. History charges: tx with occurred_on < cycle start (e.g.
  //      retroactive log). Still real debt until paid.
  // Payment (last_paid_at) drops all charges before that date —
  // they were settled by the payment.
  let outstanding_amount = Number(card.outstanding_amount || 0);
  if (txs && txs.length) {
    const cardNumericId = card.server_id ?? null;
    const cardLocalId = card.local_uuid || null;
    // cutoffIso: if paid, charges before this date are already
    // settled. Otherwise null (no cutoff, include everything).
    const cutoffIso = (paid_in_cycle && card.last_paid_at)
      ? (card.last_paid_at.length === 10
          ? card.last_paid_at
          : new Date(card.last_paid_at).toISOString().slice(0, 10))
      : null;
    let sum = 0;
    const seen = new Set();
    for (const t of txs) {
      if (t.deleted || t.type !== 'expense') continue;
      if (!t.occurred_on) continue;
      if (cutoffIso && t.occurred_on < cutoffIso) continue;
      const matchesByLocalUuid = cardLocalId && t.credit_card_local_uuid === cardLocalId;
      const matchesByServerId  = cardNumericId && t.credit_card_id === cardNumericId;
      if (!matchesByLocalUuid && !matchesByServerId) continue;
      // Deduplicate if a tx carries BOTH identifiers (Phase 49e sets
      // credit_card_local_uuid; older code paths only set server FK).
      if (seen.has(t.local_uuid || t.id)) continue;
      seen.add(t.local_uuid || t.id);
      sum += Number(t.amount || 0);
    }
    outstanding_amount = outstanding_amount + sum;
  }

  return {
    ...card,
    next_due_iso: toIso(nextDue.getFullYear(), nextDue.getMonth(), nextDue.getDate()),
    days_until_due: daysUntil,
    cycle_start_iso,
    cycle_end_iso,
    cycle_label: `${cycle_start_iso} 至 ${cycle_end_iso}`,
    grace_days_remaining,
    is_overdue,
    paid_in_cycle,
    urgency,
    outstanding_amount,
  };
}

// ─── Net Worth (transaction-based, no credit card balance) ─────────────

function computeNetWorth(txs) {
  let income = 0, expense = 0;
  for (const t of txs) {
    if (t.deleted) continue;
    if (!t.occurred_on) continue;
    if (t.type === 'income') income  += Number(t.amount || 0);
    if (t.type === 'expense') expense += Number(t.amount || 0);
  }
  return { balance: income - expense, total_income: income, total_expense: expense };
}

// ─── Public entry: compute everything for one period ────────────────────

export async function computeDashboard({ ym, categoryId = null, range = 'month', txs: txsOverride = null } = {}) {
  const [fetched, categories, budgets, cards] = await Promise.all([
    txsOverride !== null ? Promise.resolve(txsOverride) : data.getTransactions({ limit: 5000 }),
    data.getCategories(),
    data.getBudgets(),
    data.getCreditCards(),
  ]);
  const txs = fetched;

  // Resolved period: default to today (day mode) or current month.
  let period = ym;
  if (!period) {
    const now = new Date();
    if (range === 'day') period = ymdKey(now);
    else if (range === 'year') period = `${now.getFullYear()}`;
    else if (range === '5y') period = `${now.getFullYear()}`;
    else period = ymKey(now);
  }

  // Phase 71: pick a trend anchor and YoY offset per range.
  //   month (default) → 6 buckets, no YoY (only 1 month vs 1 month = trivial)
  //   day             → 7 DAILY buckets anchored on the selected day (Phase 77d
  //                     previously fell through to month buckets which showed
  //                     3月-8月 on the X axis even when range=day)
  //   year            → 12 buckets anchored on Dec, YoY = prior year (-1)
  //   5y              → 60 buckets anchored on Dec(endY), YoY = prior 5y (-5)
  // Phase 72: also build the overlay shape (per-year-per-month) for year + 5y.
  //   month + day → overlay = null (single-year data, nothing to overlay).
  const trendAnchor = (range === 'year' || range === '5y') ? `${period}-12`
                    : range === 'day' ? period
                    : period;
  const trendMonths = range === '5y' ? 60 : range === 'year' ? 12 : range === 'day' ? 0 : 6;
  const yoyOffsetYears = range === 'year' ? 1 : range === '5y' ? 5 : 0;
  const timelineTrend = range === 'day'
    ? computeDailyTrend(txs, period, 7, categoryId)
    : computeMonthlyTrend(txs, trendAnchor, trendMonths, categoryId, yoyOffsetYears, 'timeline');
  const overlayTrend = (range === 'year' || range === '5y')
    ? computeMonthlyTrend(txs, trendAnchor, 12, categoryId, yoyOffsetYears, 'overlay')
    : { overlay: null };

  return {
    kpi: computeKpi(txs, period, range, categoryId),
    // Backward-compat: `monthly_trend` keeps the same flat-array shape
    // it had before Phase 71 (just the current buckets). Phase 71 also
    // exposes `monthly_trend_prior` for the YoY overlay on the chart.
    monthly_trend: timelineTrend.current,
    monthly_trend_prior: timelineTrend.prior,
    // Phase 72: per-year overlay for the "By Year" trend view.
    monthly_trend_overlay: overlayTrend.overlay,
    category_breakdown: computeCategoryBreakdown(txs, categories, period, 'expense', categoryId),
    budget_alerts: computeBudgetAlerts(txs, categories, budgets, period, range, categoryId),
    top_categories_30d: computeTopCategories30d(txs, categories),
    net_worth: computeNetWorth(txs),
    // Phase 49f: pass `txs` so computeDueInfo can auto-calc the card's
    // outstanding_amount by joining transactions (mirrors server SQL).
    credit_cards: cards.map(c => computeDueInfo(c, new Date(), txs)),
    period_label: range === 'year' || range === '5y' ? period : period,
  };
}